import React from 'react';
import {
    SendRequestButton,
    SubmitButtonsWrapper
} from '~components/AdServiceBooking/SubmitButton/SubmitButton.components';
import { StyledLink } from '~components/shared';
import { bookingSummaryKeys } from '~tracking';
import { BOOKING_SUMMARY_LOCAL_STORAGE } from '~components/BookingSummary/constants';
import { TestId } from '~testId';
import { useAppContext } from '~state/App.context';
import { useIntl } from '@olxpwa-lib/i18n/client';
import { Platform } from '~types';
import { AdData } from '@olxpwa-lib/utils-ad/types';
import BookingSubmitButtonContainer from '~components/BookingSubmitButton';
import { useFormContext } from 'react-hook-form';
import { BookingCalendarFormValue } from '~components/AdServiceBooking/types';

type SubmitButtonType = {
    ad: AdData;
    isRequestButtonDisabled: boolean;
    isLoading: boolean;
    displayBlockDatesFeature: boolean;
    enableBookingSummaryFeature: boolean;
    isShortTermRentals: boolean;
    renderAvailabilityCalendar: boolean;
    renderBookingSubmitMobile?: (element: JSX.Element) => void;
    isDateInBlockedDates: boolean;
};

export const SubmitButton = ({
    ad,
    isRequestButtonDisabled,
    isLoading,
    displayBlockDatesFeature,
    enableBookingSummaryFeature,
    isShortTermRentals,
    renderAvailabilityCalendar,
    renderBookingSubmitMobile,
    isDateInBlockedDates
}: SubmitButtonType) => {
    const { formatMessage } = useIntl();
    const { tracker, appConfig } = useAppContext();
    const { routingAliases, platform } = appConfig;
    const { trackEvent } = tracker;
    const { watch } = useFormContext<BookingCalendarFormValue>();

    const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);

    const submitButtonTitle = {
        id: displayBlockDatesFeature
            ? isDateInBlockedDates
                ? 'service.booking.ad.enable.dates.request.button'
                : 'service.booking.ad.disable.dates.request.button'
            : renderAvailabilityCalendar
            ? 'service.booking.ad.request.button.reservation'
            : 'service.booking.ad.request.button.availiblity',
        defaultMessage: displayBlockDatesFeature
            ? isDateInBlockedDates
                ? 'Unblock selected dates'
                : 'Block the dates as unavailable'
            : renderAvailabilityCalendar
            ? 'Request a reservation'
            : 'Send query about availability'
    };

    const onClickGoToBookingSummary = () => {
        trackEvent(bookingSummaryKeys.trackEvents.bookingCalendarStart);
        localStorage.setItem(
            BOOKING_SUMMARY_LOCAL_STORAGE,
            JSON.stringify({
                ad,
                values: {
                    dateFrom,
                    dateTo
                },
                // 15 min timestamp
                timestamp: new Date().getTime() + 900000
            })
        );
    };

    return (
        <>
            {(platform !== Platform.MOBILE || displayBlockDatesFeature) && (
                <>
                    {!displayBlockDatesFeature &&
                    enableBookingSummaryFeature ? (
                        <SubmitButtonsWrapper>
                            <SendRequestButton
                                onClick={onClickGoToBookingSummary}
                                disabled={isRequestButtonDisabled}
                            >
                                <StyledLink
                                    to={{
                                        pathname: `/${routingAliases.myolx}/booking-summary/${ad.id}`
                                    }}
                                    disabled={isRequestButtonDisabled}
                                >
                                    {formatMessage({
                                        id: 'service.booking.summary.proceed.button',
                                        defaultMessage: 'Proceed with request'
                                    })}
                                </StyledLink>
                            </SendRequestButton>
                        </SubmitButtonsWrapper>
                    ) : (
                        <SubmitButtonsWrapper>
                            <SendRequestButton
                                type="submit"
                                loading={isLoading}
                                data-testid={
                                    TestId.bookingCalendarSubmitButtonDesktop
                                }
                                disabled={isRequestButtonDisabled}
                            >
                                {formatMessage({
                                    ...submitButtonTitle
                                })}
                            </SendRequestButton>
                        </SubmitButtonsWrapper>
                    )}
                </>
            )}

            {platform === Platform.MOBILE && (
                <BookingSubmitButtonContainer
                    showAvailability={renderAvailabilityCalendar}
                    ad={ad}
                    isShortTermRentals={isShortTermRentals}
                    renderBookingSubmitMobile={renderBookingSubmitMobile}
                />
            )}
        </>
    );
};
